import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { ArrowLeftRight, AlertCircle, Copy } from 'lucide-react';
import { converterToolApi } from '../../lib/toolsApi';

const WIDTHS = [8, 16, 32, 64];

const DataConverter = () => {
  const [value, setValue] = useState('1234ABCD');
  const [base, setBase] = useState('hex');
  const [width, setWidth] = useState(32);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const calculate = async () => {
    setError(null);
    setResult(null);
    try {
      const outcome = await converterToolApi.convert(value, base, width);
      setResult(outcome);
    } catch (e) {
      setError(e.message);
    }
  };

  const fmtBytes = (bytes) => bytes.map((b) => b.toString(16).toUpperCase().padStart(2, '0')).join(' ');

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="technical-card lg:col-span-3">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-autoxium-blue" />
            Data Converter
          </CardTitle>
          <CardDescription>
            Convert between hex, decimal, and binary, and view big-endian / little-endian byte order.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Value</Label>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="font-mono"
                placeholder="e.g. 1234ABCD"
                data-testid="converter-value-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Input Base</Label>
              <Select value={base} onValueChange={setBase}>
                <SelectTrigger data-testid="converter-base-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hex">Hexadecimal</SelectItem>
                  <SelectItem value="dec">Decimal</SelectItem>
                  <SelectItem value="bin">Binary</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Width</Label>
            <Select value={String(width)} onValueChange={(w) => setWidth(Number(w))}>
              <SelectTrigger data-testid="converter-width-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WIDTHS.map((w) => (
                  <SelectItem key={w} value={String(w)}>{w}-bit ({w / 8} byte{w / 8 > 1 ? 's' : ''})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive" data-testid="converter-error">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}

          <Button onClick={calculate} className="btn-premium w-full" data-testid="converter-calculate-btn">
            Calculate
          </Button>
        </CardContent>
      </Card>

      <Card className="technical-card lg:col-span-2 h-fit">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Result</CardTitle>
        </CardHeader>
        <CardContent>
          {!result ? (
            <p className="text-sm text-muted-foreground">Enter a value and click Calculate.</p>
          ) : (
            <div className="space-y-4" data-testid="converter-result">
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Hex</p>
                <p className="font-mono text-lg font-bold text-autoxium-blue break-all">{result.hex}</p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Decimal</p>
                <p className="font-mono text-sm break-all">{result.decimal}</p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Binary</p>
                <p className="font-mono text-xs break-all text-muted-foreground">{result.binary}</p>
              </div>
              <div className="pt-4 border-t border-structure space-y-2">
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Big-Endian Bytes</p>
                  <p className="font-mono text-sm break-all flex items-center gap-2">
                    <Copy className="h-3 w-3 text-muted-foreground shrink-0" />
                    {fmtBytes(result.bytesBE)}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Little-Endian Bytes</p>
                  <p className="font-mono text-sm break-all flex items-center gap-2">
                    <Copy className="h-3 w-3 text-muted-foreground shrink-0" />
                    {fmtBytes(result.bytesLE)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DataConverter;
